import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acknowledged) {
      setError("Please acknowledge the subscription terms to continue.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await register(name, email, password);
      setSubmitted(true);
      toast.success("Request submitted for review.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Card className="p-10 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-primary" data-testid="register-pending-icon" />
            <h1 className="font-display text-2xl font-semibold tracking-tight mt-4">Awaiting approval</h1>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              Thanks {name.split(" ")[0] || "there"} — your account request has been submitted. An admin will review it shortly and you'll be able to sign in once approved.
            </p>
            <div className="mt-8 flex flex-col gap-2">
              <Button className="rounded-full" onClick={() => navigate("/")} data-testid="register-pending-home-btn">Back to home</Button>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="register-pending-login-btn">Already approved? Sign in</Button>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 sm:px-6 py-10 sm:py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Card className="p-6 sm:p-8 lg:p-10">
          <p className="overline text-primary mb-3">Join the Academy</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Apply to train</h1>
          <p className="text-sm text-muted-foreground mt-2">New members are reviewed by an admin before access is granted.</p>
          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="h-11" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="register-password-input" />
              <p className="text-xs text-muted-foreground">Minimum 6 characters.</p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none rounded-lg border border-border bg-muted/30 p-4 hover:border-primary/40 transition-colors">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="h-4 w-4 accent-primary mt-0.5 shrink-0"
                data-testid="register-subscription-checkbox"
              />
              <span className="text-sm text-foreground leading-relaxed">
                I understand the Academy is a subscription service — <span className="font-semibold">free for current 1:1 clients</span>, otherwise <span className="font-semibold">$100 per month</span>.
              </span>
            </label>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={submitting || !acknowledged} className="w-full h-11 rounded-full" data-testid="register-submit-btn">
              {submitting ? "Submitting request..." : "Join the Academy"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="register-go-login-link">Sign in</Link>
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
